/**
 * Anti-corruption layer helpers shared by every entity mapper.
 *
 * The backend speaks camelCase enums and ISO dates; the frontend DISPLAY types
 * in src/app/types.ts use kebab enums (legally-required, needs-update) and
 * human display strings ("1 Sep 2026", "Full-time"). These converters live in
 * one place so every mapper reads the same.
 */

import type { CaseRisk, CaseStatus, DocCategory, DocStatus } from '../../app/types';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Format an ISO date ("yyyy-MM-dd") or ISO timestamp as "1 Sep 2026".
 * Date-only strings are parsed positionally to avoid timezone off-by-one.
 * Returns '' for missing/unparseable input.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  let year: number;
  let month: number;
  let day: number;
  if (dateOnly) {
    year = Number(dateOnly[1]);
    month = Number(dateOnly[2]);
    day = Number(dateOnly[3]);
  } else {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }
  const label = MONTHS[month - 1];
  if (!label) return '';
  return `${day} ${label} ${year}`;
}

/* ------------------------------------------------------- Document category */

const CATEGORY_TO_KEBAB: Record<string, DocCategory> = {
  legallyRequired: 'legally-required',
  essential: 'essential',
  bestPractice: 'best-practice',
  optional: 'optional',
  notRelevant: 'not-relevant',
  shouldNotHave: 'should-not-have',
};

/** camelCase backend DocCategory -> kebab display DocCategory. */
export function categoryToKebab(category: string): DocCategory {
  return CATEGORY_TO_KEBAB[category] ?? 'optional';
}

/* --------------------------------------------------------- Document status */

const STATUS_TO_KEBAB: Record<string, DocStatus> = {
  ok: 'ok',
  needsUpdate: 'needs-update',
  missing: 'missing',
};

/** camelCase backend DocStatus -> kebab display DocStatus. */
export function statusToKebab(status: string): DocStatus {
  return STATUS_TO_KEBAB[status] ?? 'missing';
}

/* ------------------------------------------------------------ Contract type */

const CONTRACT_TYPE_TO_DISPLAY: Record<string, string> = {
  fullTime: 'Full-time',
  partTime: 'Part-time',
  casual: 'Casual',
};

/** camelCase backend ContractType -> display string ("Full-time"). */
export function contractTypeToDisplay(contractType: string): string {
  return CONTRACT_TYPE_TO_DISPLAY[contractType] ?? contractType;
}

/* ----------------------------------------------------------------- Case kind */

const CASE_KIND_TO_DISPLAY: Record<string, string> = {
  grievance: 'Grievance',
  disciplinary: 'Disciplinary',
  sicknessAbsence: 'Absence',
  leaverOffboarding: 'Leaver & offboarding',
  flexibleWorking: 'Flexible working',
  probation: 'Probation',
  compliance: 'Compliance',
  conduct: 'Conduct',
  starterOnboarding: 'Starter & onboarding',
  other: 'Other',
};

/** camelCase backend CaseKind -> display string ("Leaver & offboarding"). */
export function caseKindToDisplay(kind: string): string {
  return CASE_KIND_TO_DISPLAY[kind] ?? kind;
}

/**
 * Case status/risk already share the display union values (lowercase kebab-free
 * words), so these are identity casts — kept as named helpers so mappers read
 * consistently and any future divergence has one place to change.
 */
export function caseStatusToDisplay(status: string): CaseStatus {
  return status as CaseStatus;
}

export function caseRiskToDisplay(risk: string): CaseRisk {
  return risk as CaseRisk;
}

/* ----------------------------------------------------------------- Plan */

/** Capitalise the backend plan enum for display ("business" -> "Business"). */
export function planToDisplay(plan: string): string {
  if (!plan) return '';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}
